/** localStorage helpers for nurse attendance drafts (session+date+class). */

const CLASS_KEY = 'syms_attendance_last_class';
const TOUCH_KEY = 'syms_attendance_touch_mode';
const DRAFT_PREFIX = 'syms_attendance_draft_';

export function draftStorageKey(sessionId, date, classKey) {
  return `${DRAFT_PREFIX}${sessionId || 'none'}_${date || ''}_${classKey || 'all'}`;
}

export function readLastClassFilter() {
  try {
    const v = localStorage.getItem(CLASS_KEY);
    return v == null ? '' : v;
  } catch {
    return '';
  }
}

export function writeLastClassFilter(classKey) {
  try {
    localStorage.setItem(CLASS_KEY, classKey == null ? '' : String(classKey));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Explicit touch-mode preference for Surface / tablet nurses.
 * Returns null when unset (caller should default from `(any-pointer: coarse)`).
 * Do not key cards off CSS width — Surface Pro is ≥900px in both orientations.
 */
export function readTouchModePreference() {
  try {
    const v = localStorage.getItem(TOUCH_KEY);
    if (v === '1' || v === 'true') return true;
    if (v === '0' || v === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

export function writeTouchModePreference(enabled) {
  try {
    localStorage.setItem(TOUCH_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function readAttendanceDraft(sessionId, date, classKey) {
  try {
    const raw = localStorage.getItem(draftStorageKey(sessionId, date, classKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      local: parsed.local && typeof parsed.local === 'object' ? parsed.local : {},
      dirty: parsed.dirty && typeof parsed.dirty === 'object' ? parsed.dirty : {},
    };
  } catch {
    return null;
  }
}

export function writeAttendanceDraft(sessionId, date, classKey, local, dirty) {
  try {
    const key = draftStorageKey(sessionId, date, classKey);
    if (!dirty || Object.keys(dirty).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify({ local, dirty }));
  } catch {
    /* ignore */
  }
}

export function clearAttendanceDraft(sessionId, date, classKey) {
  try {
    localStorage.removeItem(draftStorageKey(sessionId, date, classKey));
  } catch {
    /* ignore */
  }
}

/** Normalize API time (`HH:MM:SS` or `HH:MM`) to `HH:MM`, or '' when blank. */
export function normalizeHhmm(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (!s) return '';
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  return `${String(Number(m[1])).padStart(2, '0')}:${m[2]}`;
}

/** Current local time rounded to nearest 5 minutes as HH:MM. */
export function nowRoundedToFiveMinutes(date = new Date()) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  let mins = d.getMinutes();
  const rounded = Math.round(mins / 5) * 5;
  if (rounded === 60) {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  } else {
    d.setMinutes(rounded);
  }
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Map a stored AttendanceRecord to UI status (present / absent / unmarked only).
 * Late/early are independent fields — do not collapse them into status.
 */
export function recordToUiStatus(rec) {
  if (!rec || !rec.status) return '';
  if (rec.status === 'absent') return 'absent';
  if (rec.status === 'present') return 'present';
  // Legacy cached payloads that still send late/early as status
  if (rec.status === 'late' || rec.status === 'early') return 'present';
  return '';
}

export function statusLabel(s) {
  switch (s) {
    case 'present':
      return 'Present';
    case 'absent':
      return 'Absent';
    default:
      return '—';
  }
}

export function statusColor(s) {
  switch (s) {
    case 'present':
      return 'success';
    case 'absent':
      return 'error';
    default:
      return 'default';
  }
}

export function classroomPickerLabel(c) {
  if (!c) return '';
  return c.picker_label || c.label || c.class_num || '';
}
